import { EditIcon, ImageIcon, WarningIcon, EditSquareIcon } from '../../../../../assets/icons';
import { updateProfileInfoThunk } from '../../../../../store/workspace/thunks';
import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { objectURLToFile } from '../../../../../utils/generalHelpers';
import { uploadFileThunk } from '../../../../../store/general/thunks';
import { useDispatch, useSelector } from '../../../../../store/store';
import { ListItem } from '../../../../../store/general/types';
import { useNavigate } from 'react-router-dom';
import DropDown from '../../../../../components/DropDown';
import { companySizeList } from '../../../../../configs';
import styles from './style.module.scss';
import Modal from '../../../../../components/Modal';
import Input from '../../../../../components/Input';
import { v4 as uuid } from 'uuid';
import {
    selectProfile,
    selectLogoImage,
    selectCroppedCompanyLogo,
    selectUpdatingProfileInfo,
} from '../../../../../store/workspace/selectors';
import {
    setCompanyLogo,
    setCompanyLogoModal,
    setCroppedCompanyLogo,
    setEditCompanyInfoModal,
} from '../../../../../store/workspace/slice';

/** Where a freshly cropped logo is stored, keyed by the profile's logo name. */
const LOGO_FOLDER = 'companies-logo-images';

export default function EditCompanyInfo() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const logo = useSelector(selectLogoImage);
    const profile = useSelector(selectProfile);
    const isSaving = useSelector(selectUpdatingProfileInfo);
    const croppedCompanyLogo = useSelector(selectCroppedCompanyLogo);
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    const [isValidURL, setIsValidURL] = useState(true);
    const [uploadingLogo, setUploadingLogo] = useState(false);
    const [city, setCity] = useState(profile?.city || '');
    const [state, setState] = useState(profile?.state || '');
    const [address, setAddress] = useState(profile?.address || '');
    const [country, setCountry] = useState(profile?.country || '');
    const [zipCode, setZipCode] = useState(profile?.zipCode || '');
    const [website, setWebsite] = useState(profile?.companyWebsite || '');
    const [companySize, setCompanySize] = useState<ListItem | null>(profile?.companySize || null);

    /** A logo picked but never confirmed must not leak into the next session. */
    useEffect(() => {
        return () => {
            dispatch(setCroppedCompanyLogo(null));
            dispatch(setCompanyLogo(null));
        };
    }, []);

    const closeModal = () => dispatch(setEditCompanyInfoModal(false));

    const navigateToSupport = () => {
        closeModal();
        navigate('/workspace/support');
    };

    const triggerFileSelect = () => {
        fileInputRef.current?.click();
    };

    const onSelectFile = (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.addEventListener('load', () => {
            dispatch(setCompanyLogo(reader.result as string));
            dispatch(setCompanyLogoModal(true));
        });
        reader.readAsDataURL(file);
    };

    const submit = async () => {
        const body = {
            ...profile,
            companyWebsite: website,
            city,
            state,
            zipCode,
            country,
            address,
            companySize,
        };

        if (croppedCompanyLogo) {
            setUploadingLogo(true);
            const companyLogoKey = profile?.companyLogo || `${uuid()}.png`;
            const logoImageFile = await objectURLToFile(croppedCompanyLogo, companyLogoKey, 'image/png');

            logoImageFile && await dispatch(uploadFileThunk({
                file: logoImageFile,
                accessLevel: 'guest',
                fileName: `${LOGO_FOLDER}/${companyLogoKey}`,
            }));

            body.companyLogo = companyLogoKey;
        }

        dispatch(updateProfileInfoThunk(body));
    };

    const previewLogo = croppedCompanyLogo || logo;

    return (
        <Modal
            size='md'
            actions='both'
            closeFunc={closeModal}
            icon={<EditSquareIcon />}
            childClassName={styles.wrapper}
            title='Edit company information'
            dismissButton={{
                text: 'Cancel',
                type: 'Outline-Primary',
                onClick: closeModal,
            }}
            confirmButton={{
                text: 'Confirm',
                type: 'Primary',
                onClick: submit,
                loading: isSaving || uploadingLogo,
                disabled: !city ||
                    !isValidURL ||
                    !state ||
                    !zipCode ||
                    !country ||
                    !address ||
                    !companySize,
            }}
        >
            <div className={styles.content}>
                <div className={styles.content_form}>
                    <Input
                        type='text'
                        label='Company website url'
                        value={website}
                        validation='URL'
                        setIsValid={setIsValidURL}
                        onChange={setWebsite}
                    />
                    <DropDown
                        idKey='id'
                        titleKey='title'
                        label='Company Size'
                        setItem={setCompanySize}
                        selectedItem={companySize}
                        dropDownList={companySizeList}
                    />
                    <Input
                        type='text'
                        label='Address'
                        placeholder=''
                        value={address}
                        onChange={setAddress}
                    />
                    <div className={styles.grid}>
                        <Input
                            type='text'
                            label='City'
                            value={city}
                            onChange={setCity}
                        />
                        <Input
                            type='text'
                            label='State'
                            value={state}
                            onChange={setState}
                        />
                        <Input
                            type='text'
                            label='Country'
                            value={country}
                            onChange={setCountry}
                        />
                        <Input
                            type='text'
                            label='Zip Code'
                            value={zipCode}
                            onChange={setZipCode}
                        />
                    </div>
                </div>

                <div className={styles.content_logo}>
                    <span>Company logo</span>
                    <div className={styles.content_logo_placeholder} onClick={triggerFileSelect}>
                        <input
                            type='file'
                            accept='image/*'
                            ref={fileInputRef}
                            onChange={onSelectFile}
                            style={{ display: 'none' }}
                        />
                        {previewLogo ? <img src={previewLogo} alt='' /> : <ImageIcon />}
                        <div className={styles.content_logo_placeholder_overlay}>
                            <EditIcon width={46} height={46} />
                        </div>
                    </div>
                </div>

                <div className={styles.description}>
                    <WarningIcon />
                    <span className={styles.description_text}>
                        To update your company name,
                        please reach out to our <b onClick={navigateToSupport}>support</b> team.
                    </span>
                </div>
            </div>
        </Modal>
    );
};
